// Optional pieces of a large GTFS network. A server derives these from the
// same accepted network generation it already serves; a client chooses pieces
// whose line coverage intersects its current map view.

import type { GtfsGeometryFile, GtfsNetworkFile } from './types.ts';
import type { GtfsHours } from './hours.ts';

export type GtfsPartitionBox = readonly [west: number, south: number, east: number, north: number];

export interface GtfsPartition {
  readonly id: string;
  readonly minor: boolean;
  readonly coverage: readonly GtfsPartitionBox[];
}

export interface GtfsPartitionManifest {
  readonly built: string;
  readonly parts: readonly GtfsPartition[];
}

const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isBox = (value: unknown): value is GtfsPartitionBox =>
  Array.isArray(value) &&
  value.length === 4 &&
  value.every((coordinate) => typeof coordinate === 'number' && Number.isFinite(coordinate)) &&
  value[0] <= value[2] &&
  value[1] <= value[3];

export function isPartitionManifest(value: unknown): value is GtfsPartitionManifest {
  if (!isRecord(value) || typeof value['built'] !== 'string' || !Array.isArray(value['parts'])) return false;
  const ids = new Set<string>();
  return value['parts'].every((part) => {
    if (
      !isRecord(part) ||
      typeof part['id'] !== 'string' ||
      !/^[A-Za-z0-9_-]+$/u.test(part['id']) ||
      ids.has(part['id']) ||
      typeof part['minor'] !== 'boolean' ||
      !Array.isArray(part['coverage']) ||
      !part['coverage'].every(isBox)
    )
      return false;
    ids.add(part['id']);
    return true;
  });
}

export function intersectsPartition(a: GtfsPartitionBox, b: GtfsPartitionBox): boolean {
  return a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];
}

export function selectedPartitions(
  manifest: GtfsPartitionManifest,
  bounds: GtfsPartitionBox,
  includeMinor: boolean,
): readonly string[] {
  return manifest.parts
    .filter(
      (part) =>
        (includeMinor || !part.minor) && part.coverage.some((coverage) => intersectsPartition(bounds, coverage)),
    )
    .map((part) => part.id)
    .toSorted();
}

export function partitionManifest(
  file: GtfsNetworkFile,
  partOf: (lineId: string) => string,
  minor: ReadonlySet<string>,
): GtfsPartitionManifest {
  const grouped = new Map<string, { minor: boolean; coverage: GtfsPartitionBox[] }>();
  for (const [lineId, patterns] of Object.entries(file.lines)) {
    const id = partOf(lineId);
    let part = grouped.get(id);
    if (!part) {
      part = { minor: true, coverage: [] };
      grouped.set(id, part);
    }
    if (!minor.has(lineId)) part.minor = false;
    let west = Infinity;
    let south = Infinity;
    let east = -Infinity;
    let north = -Infinity;
    for (const pattern of patterns) {
      for (const stationId of pattern.ids) {
        const station = file.stations[stationId];
        if (!station) continue;
        west = Math.min(west, station[2]);
        south = Math.min(south, station[1]);
        east = Math.max(east, station[2]);
        north = Math.max(north, station[1]);
      }
    }
    if (Number.isFinite(west)) part.coverage.push([west, south, east, north]);
  }
  const parts: GtfsPartition[] = [];
  for (const [id, part] of grouped) parts.push({ id, minor: part.minor, coverage: part.coverage });
  return {
    built: file.built,
    parts: parts.toSorted((a: GtfsPartition, b: GtfsPartition) => a.id.localeCompare(b.id)),
  };
}

function mergeHours(files: readonly GtfsNetworkFile[]): GtfsHours | undefined {
  const merged: Record<string, Record<string, readonly number[]>> = {};
  let found = false;
  for (const file of files) {
    if (!file.hours) continue;
    found = true;
    for (const [day, lines] of Object.entries(file.hours)) Object.assign((merged[day] ??= {}), lines);
  }
  return found ? merged : undefined;
}

export function mergeNetworkParts(built: string, files: readonly GtfsNetworkFile[]): GtfsNetworkFile {
  const stations: Record<string, GtfsNetworkFile['stations'][string]> = {};
  const lines: Record<string, GtfsNetworkFile['lines'][string]> = {};
  const shapes: Record<string, readonly string[]> = {};
  let hasShapes = false;
  for (const file of files) {
    Object.assign(stations, file.stations);
    Object.assign(lines, file.lines);
    if (file.shapes) {
      hasShapes = true;
      Object.assign(shapes, file.shapes);
    }
  }
  const hours = mergeHours(files);
  return {
    built,
    stations,
    lines,
    ...(hasShapes ? { shapes } : {}),
    ...(hours ? { hours } : {}),
  };
}

export function mergeGeometryParts(files: readonly GtfsGeometryFile[]): GtfsGeometryFile {
  const segments: Record<string, Readonly<Record<string, string>>> = {};
  for (const file of files) Object.assign(segments, file.segments);
  return {
    source: files
      .map((file) => file.source)
      .filter((source) => source !== '')
      .join('; '),
    format: files.find((file) => file.format !== '')?.format ?? '',
    segments,
  };
}
